import { Headline } from "@components/cms/headline/Headline";
import { PageSeo } from "@components/seo/PageSeo";
import { getSiteSettings } from "@config/site/site-api-settings";
import { getPage } from "@contentful-api/queries/pages/pageService";
import type {
    IModuleHeadline,
    IModuleHeadlineFields,
    IPageGeneric,
    ISiteSettings,
} from "@contentful-api/types/contentful";
import type { PagePath } from "@lib/constants/pagePaths";
import { getPagePathByLocale } from "@lib/constants/pagePaths";
import { COMMON_NEXT_CACHE_TTL } from "@lib/constants/revalidation";
import { ModulesRenderer } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { PageTypes } from "@lib/enums/PageTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { getCompleteSlug } from "@lib/utils/getCompleteSlug";
import { Box } from "@ui/components/layout/box/Box";
import { deserialize, serialize } from "@ungap/structured-clone";
import type { GetStaticPropsContext, PreviewData } from "next";
import type { ParsedUrlQuery } from "querystring";
import type { FC } from "react";

export type GenericPageProps = Awaited<
    ReturnType<typeof generateGetStaticPropsForGenericPage>
>["props"];

export const GenericPage: FC<GenericPageProps> = ({
    pathName,
    serializedPage,
    serializedSiteSettings,
}) => {
    const page: IPageGeneric = deserialize(serializedPage);
    const siteSettings: ISiteSettings = deserialize(serializedSiteSettings);
    const authorizationInvitation = siteSettings?.fields?.authorizationInvitation;
    const pageModules = page?.fields?.modules;
    const seoMetadata = page?.fields?.seoMetadata;
    const titleSize = page?.fields?.titleSize || "Extra Large";
    const descriptionSize = page?.fields?.descriptionSize || "Normal";
    const pageHeadingTitle = page?.fields?.title;
    const pageHeadingCTAs = page?.fields?.callToActions;
    const description = page?.fields?.desc;

    return (
        <>
            {seoMetadata && <PageSeo seoModule={seoMetadata} pathName={pathName} />}
            {(pageHeadingTitle || description) && (
                <Headline
                    module={
                        {
                            fields: {
                                title: pageHeadingTitle,
                                desc: description,
                                callToActions: pageHeadingCTAs,
                                // We need to cast because the types are not exactly the same
                                titleSize: titleSize as IModuleHeadlineFields["titleSize"],
                                descriptionSize,
                            },
                        } as IModuleHeadline
                    }
                    isModule={false}
                />
            )}
            <Box px={{ base: 3, xl: 4 }}>
                <ModulesRenderer
                    modules={pageModules}
                    authorizationInvitation={authorizationInvitation}
                />
            </Box>
        </>
    );
};

export const generateGetStaticPropsForGenericPage = async (
    context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>,
    pagePath: PagePath
) => {
    const startTime = performance.now();
    const { locale, params } = context;

    const contentfulStartTime = performance.now();
    const { data, error } = await getPage({
        slug: params?.slug,
        locale,
        basePath: pagePath,
        localeTagId: getSiteSettings.contentfulLocaleTagId,
        textCollection: ["shared"],
    });
    Logger.info(ServiceType.CONTENTFUL, "GenericPage Contentful Query", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - contentfulStartTime,
        pageType: PageTypes.GENERIC,
    });

    if (error) {
        throw error;
    }

    if (!data?.pagePath) {
        return {
            notFound: true,
            revalidate: COMMON_NEXT_CACHE_TTL,
        };
    }

    const contentfulPageType = data.pagePath.fields.page.sys.contentType.sys.id;

    const siteSettingsContent = data?.siteSettings.siteSettings;
    const pageData = data?.pagePath?.fields?.page;

    // We need to serialize the data so next doesn't explode trying to stringify circular references
    const serializedPage = serialize(pageData);
    const serializedSiteSettings = serialize(siteSettingsContent);
    const serializedSharedMicrocopySet = serialize(data?.siteSettings["shared"]);
    const pathName = getCompleteSlug(params?.slug, getPagePathByLocale(pagePath));

    Logger.info(ServiceType.NEXT_API, "GenericPage Total Time", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType: PageTypes.GENERIC,
    });
    const props = {
        serializedSiteSettings,
        serializedPage,
        pathName,
        pageType:
            contentfulPageType === "pageTwoColumns"
                ? PageTypes.CUSTOMER_SERVICE
                : PageTypes.GENERIC,
        serializedSharedMicrocopySet,
    };

    if (pagePath.en === "/reset-password") {
        return {
            props,
        };
    }
    return {
        props,
        revalidate: 300,
    };
};
